import { useQueryClient } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  BidResponseExtractionEvent_EventTypeEnum_0,
  BidResponseExtractionStatusSubscription,
  BidResponseExtractionStatusSubscriptionVariables,
  BidResponsesQuery,
} from "lib/generated/app-service-gql/graphql";
import { deduplicateByKey } from "lib/utils";
import { useSubscription } from "../../useGraphQlClient";
import { updateBidQuery } from "../bids/updateBidCache";
import { bidResponsesKey } from "./useBidResponses";

// adding this so it generates the types
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _bidResponseExtractionStatus = graphql(`
  subscription bidResponseExtractionStatus($input: BidResponseExtractionRequest!) {
    bidResponseExtractionStatus(BidResponseExtractionRequest: $input) {
      resourceId
      bidId
      eventType
      bidResponses {
        id
        title
        description
        weighting
        reference
        formattingRequirements
        section
        dueDate
        createdAt
        updatedAt
        deletedAt
        response
        stageId
        teamId
        companyId
        source {
          documentId
          title
          locations {
            pageNumber
            chunkNumber
            bbox {
              x
              y
              width
              height
            }
          }
        }
      }
      documentId
      message
    }
  }
`);

type UseFileExportStatusVars = {
  resourceId?: string;
  onDocumentProcessed: (v: {
    documentId: string;
    questionsExtracted: number;
    message: string | null;
  }) => void;
  onError: (v: { documentId: string; message: string | null }) => void;
  onStatusUpdate: (v: { documentId: string; message: string | null }) => void;
  isEnabled?: boolean;
};

export function useBidResponseExtractionStatus({
  resourceId,
  onDocumentProcessed,
  onError,
  onStatusUpdate,
  isEnabled = false,
}: UseFileExportStatusVars) {
  const queryClient = useQueryClient();

  const input = { resourceId: resourceId ?? "" };

  return useSubscription<
    BidResponseExtractionStatusSubscription,
    BidResponseExtractionStatusSubscriptionVariables
  >(
    `
subscription bidResponseExtractionStatus($input: BidResponseExtractionRequest!) {
    bidResponseExtractionStatus(BidResponseExtractionRequest: $input) {
  resourceId
      bidId
      eventType
      bidResponses {
        id
        title
        description
        weighting
        formattingRequirements
        section
        dueDate
        createdAt
        updatedAt
        deletedAt
        response
        reference
        stageId
        teamId
        companyId
        source {
          documentId
          title
          locations {
            pageNumber
            chunkNumber
            bbox {
              x
              y
              width
              height
            }
          }
        }
      }
      documentId
      message
    }
  }
`,
    { input },
    {
      enabled: Boolean(resourceId) && isEnabled,
      onData: ({
        bidResponseExtractionStatus: {
          documentId,
          eventType,
          bidResponses,
          message,
          resourceId,
          bidId,
        },
      }) => {
        if (!resourceId) {
          return;
        }

        switch (eventType) {
          case BidResponseExtractionEvent_EventTypeEnum_0.Completed: {
            if (bidResponses && bidResponses.length > 0) {
              // Update the list query cache with newly extracted responses.
              queryClient.setQueriesData<BidResponsesQuery>(
                {
                  queryKey: bidResponsesKey({
                    resourceId,
                  }),
                  exact: false,
                },
                (oldData) => {
                  if (!oldData) {
                    return {
                      bidResponses: { items: bidResponses, totalCount: bidResponses.length },
                    };
                  }

                  const newbidResponses = Array.from(
                    deduplicateByKey([...oldData.bidResponses.items, ...bidResponses], (b) => b.id),
                  );

                  return {
                    bidResponses: {
                      items: newbidResponses,
                      totalCount: newbidResponses.length,
                    },
                  };
                },
              );

              if (bidId) {
                updateBidQuery(queryClient, bidId, (prevData) => {
                  if (!prevData) return prevData;

                  const mergedBidResponses = Array.from(
                    deduplicateByKey(
                      [...(prevData.bidResponses ?? []), ...bidResponses],
                      (b) => b.id,
                    ),
                  );

                  return {
                    ...prevData,
                    bidResponses: mergedBidResponses,
                  };
                });
              }
            }
            onDocumentProcessed({
              documentId,
              questionsExtracted: bidResponses?.length || 0,
              message: message || null,
            });
            return;
          }
          case BidResponseExtractionEvent_EventTypeEnum_0.Error: {
            onError({
              documentId,
              message: message || null,
            });
            return;
          }
          case BidResponseExtractionEvent_EventTypeEnum_0.Status: {
            onStatusUpdate({
              documentId,
              message: message || null,
            });
            return;
          }
          default:
            return;
        }
      },
    },
  );
}
